import { assertNever } from 'assert-never';
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useAsyncAbortable } from 'react-async-hook';
import CustomComponentContext from '../CustomComponentContext';
import PanelContainer from './PanelContainer';
import FormPanel, { FormData } from './FormPanel';
import PromptPanel from './PromptPanel';
import SuccessPanel from './SuccessPanel';
import DeliveryInstructionsSublimeSuccess from '../DeliveryInstructionsSublimeSuccess';
import {
  getLasershipDeliveryInstructions,
  putLasershipDeliveryInstructions,
} from './api';

type PanelState = 'prompt' | 'form' | 'success' | 'narvar-success' | undefined;

const featureName = 'deliveryInstructions';

const LasershipDeliveryInstructions: FC = () => {
  const { state, signalContentLoaded, track } = useContext(
    CustomComponentContext,
  );
  const retailerMoniker = state?.pageContext.retailerMoniker;
  const trackingNumber = state?.pageContext.trackingNumbers?.[0];
  const trackingStatusCode = state?.pageContext.trackingStatusCode;
  const carrier = state?.pageContext.carrier;
  const orderNumber = state?.pageContext.orderNumbers?.[0];

  const { result: deliveryInstructions } = useAsyncAbortable(
    getLasershipDeliveryInstructions,
    [retailerMoniker, trackingNumber],
  );

  const [panel, setPanel] = useState<PanelState>();
  const [sublimeSubmit, setSublimeSubmit] = useState(false);
  const [hasProfile, sethasProfile] = useState(false);

  useEffect(() => {
    if (!deliveryInstructions) return;
    if (!trackingStatusCode) return;
    if (!signalContentLoaded) return;

    if (deliveryInstructions.narvarSubmitted) {
      setPanel('narvar-success');
      signalContentLoaded();
      return;
    }

    if (deliveryInstructions.submitted) {
      setPanel('success');
      signalContentLoaded();
      return;
    }

    // only show ourselves for certain statuses
    if (!['SHPD', 'INTR', 'EXCP'].includes(trackingStatusCode)) {
      return;
    }

    setPanel('prompt');
    signalContentLoaded();
    track({
      eventType: 'click',
      clickSubType: 'lasership_prompt_panel_view',
    });
  }, [deliveryInstructions, trackingStatusCode, signalContentLoaded, track]);

  const onStartClick = useCallback(() => {
    setPanel('form');
    track({
      eventType: 'click',
      clickSubType: 'lasership_prompt_panel_cta_click',
    });
  }, [setPanel, track]);

  const onCancelClick = useCallback(() => {
    setPanel('prompt');
  }, [setPanel]);

  const onLeaveAtChange = useCallback(() => {
    track({
      eventType: 'click',
      clickSubType: 'lasership_leave_at_input_change',
    });
  }, [track]);

  const onSubmit = useCallback(
    async (formData: FormData) => {
      track({
        eventType: 'click',
        clickSubType: 'lasership_form_submit',
      });
      if (retailerMoniker === undefined) {
        throw new Error('Expected retailerMoniker to be defined');
      }
      if (trackingNumber === undefined) {
        throw new Error('Expected trackingNumber to be defined');
      }
      if (carrier === undefined) {
        throw new Error('Expected carrier to be defined');
      }
      const { sublimeEmail, leaveAt, note, sublimeOptin } = formData;
      const instructions = [leaveAt === 'other' ? undefined : leaveAt, note]
        .filter((v) => v)
        .join('\n');
      const { hasProfile: hasSublimeProfile } =
        await putLasershipDeliveryInstructions(
          retailerMoniker,
          trackingNumber,
          orderNumber ?? '',
          instructions,
          sublimeEmail,
        );
      setSublimeSubmit(sublimeOptin ?? false);
      sethasProfile(hasSublimeProfile);
      setPanel('success');
      track({
        eventType: 'click',
        clickSubType: 'lasership_success_panel_view',
      });
    },
    [track, retailerMoniker, trackingNumber, carrier, orderNumber],
  );

  const onCloseClick = useCallback(() => {
    setPanel(undefined);
  }, [setPanel]);

  if (!panel) return null;

  return (
    <PanelContainer dismissOnClick={onCloseClick} featureName={featureName}>
      {(() => {
        switch (panel) {
          case 'prompt':
            return (
              <PromptPanel featureName={featureName} next={onStartClick} />
            );
          case 'form':
            return (
              <FormPanel
                featureName={featureName}
                onLeaveAtChange={onLeaveAtChange}
                onSubmit={onSubmit}
                onCancel={onCancelClick}
              />
            );
          case 'success':
            return sublimeSubmit ? (
              <DeliveryInstructionsSublimeSuccess hasProfile={hasProfile} />
            ) : (
              <SuccessPanel />
            );
          case 'narvar-success':
            return <DeliveryInstructionsSublimeSuccess autoApplied />;
          default:
            return assertNever(panel);
        }
      })()}
    </PanelContainer>
  );
};

export default LasershipDeliveryInstructions;
